// Light/dark for the Control Plane section only.
//
// The rest of this SPA is dark-only and cannot yet be otherwise: roughly 330
// hard-coded hex colours live outside the token system in the money-operations
// screens, all tuned for a dark ground. The Control Plane section uses tokens
// exclusively, so it can carry both themes today by setting one attribute; the
// palette itself lives in `index.html` under `[data-cp-theme="light"]` and is
// inert everywhere the attribute is absent.
//
// Preference is remembered per browser in `localStorage`. It is not sent
// anywhere and is not part of any session.

import { useCallback, useEffect, useState } from "react";
import type { Lane } from "./contract";

export type ControlPlaneTheme = "dark" | "light";

const STORAGE_KEY = "gao-admin-web:control-plane-theme";

/** The host each lane is served from. Display only — the console never selects
 *  a host for anything, and neither does the Control Plane. */
export const LANE_HOSTS: Record<Lane, string> = {
  production: "id.gao.global",
  test: "id-test.gao.domains",
  dev: "id-dev.gao.domains",
};

function readStored(): ControlPlaneTheme {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "light" ? "light" : "dark";
  } catch {
    // Private windows and blocked site data both throw here. A theme
    // preference is not worth a crash.
    return "dark";
  }
}

export function useControlPlaneTheme(): {
  theme: ControlPlaneTheme;
  toggle: () => void;
  themeProps: { "data-cp-theme": ControlPlaneTheme };
} {
  const [theme, setTheme] = useState<ControlPlaneTheme>(() => readStored());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* preference is a convenience, never a requirement */
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "light" ? "dark" : "light"));
  }, []);

  return { theme, toggle, themeProps: { "data-cp-theme": theme } };
}

/** The host for a lane, or undefined while the lane is still unknown. Never
 *  guesses — see `LaneChip`. */
export function laneHost(lane: Lane | null): string | undefined {
  return lane === null ? undefined : LANE_HOSTS[lane];
}

/** "PRODUCT" / "TEST" / "DEV" / "UNKNOWN". */
export function laneLabel(lane: Lane | null): string {
  return lane === null
    ? "UNKNOWN"
    : lane === "production"
      ? "PRODUCT"
      : lane.toUpperCase();
}
